;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import { Bars3BottomLeftIcon } from '@heroicons/react/24/outline'
import { AlertBanner } from '@qogita/ui/v2/alert-banner'
import { Anchor } from '@qogita/ui/v2/anchor'
import { BaseButton } from '@qogita/ui/v2/base-button'
import { Logo } from '@qogita/ui/v2/logo'
import clsx from 'clsx'
import Link from 'next/link'
import React, { ComponentProps, ReactNode, useState } from 'react'
import { OrderSearch } from 'src/app/(main)/_components/order-search'
import { EMAILS } from 'src/core/constants/copy'
import { ErrorBoundary } from 'src/deprecated/components/shared/ErrorBoundary'
import { IngestionBannerWrapper } from 'src/deprecated/components/shared/templates/ingestion-banner-wrapper'
import { MobileSidebar } from 'src/deprecated/components/shared/templates/Sidebar/MobileSidebar'
import { Sidebar } from 'src/deprecated/components/shared/templates/Sidebar/Sidebar'
import { NoActivePriceListsBannerWrapper } from 'src/deprecated/components/shared/templates/no-active-price-lists-banner-wrapper'
import { StockExpiryBannerWrapper } from 'src/deprecated/components/shared/templates/stock-expiry-banner-wrapper'
import { routes } from 'src/deprecated/utils/routes'

type PageLayoutProps = {
  children: ReactNode
  maxWidth?: 'NONE' | 'lg'
}

function PageLayoutRootRebranded({
  maxWidth = 'lg',
  children,
}: PageLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)

  return (
    <div className="font-outfit">
      <MobileSidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      {/* Static sidebar for desktop */}
      <div className="hidden md:fixed md:inset-y-0 md:flex md:w-[209px] md:flex-col">
        <div className="flex grow flex-col overflow-hidden border-r border-gray-300 bg-white pt-5">
          <div className="flex shrink-0 items-center self-end px-4 pb-10">
            <Link
              href={routes.dashboard}
              className="rounded-sm focus:ring-2 focus:ring-gray-800 focus:ring-offset-2 focus:outline-hidden"
            >
              <Logo
                name="qogitaSellersV2"
                className="h-[3.0625rem] w-[7.875rem]"
              />
            </Link>
          </div>
          <Sidebar />
        </div>
      </div>

      <div className="flex flex-1 flex-col md:pl-[209px]">
        <div className="sticky top-0 z-10">
          <NoActivePriceListsBannerWrapper />
          <IngestionBannerWrapper />
          <StockExpiryBannerWrapper />

          <div className="flex h-18 shrink-0 bg-white shadow-sm">
            <BaseButton
              // oxlint-disable-next-line shadcn/no-restyle -- grandfathered: pre-rollout restyle className on v2 component
              className="border-r border-gray-300 px-4 text-gray-500 focus:ring-2 focus:outline-hidden focus:ring-inset md:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3BottomLeftIcon className="h-6 w-6" aria-hidden="true" />
            </BaseButton>

            <div className="flex w-full items-center justify-center px-3.5">
              <div
                className={clsx('flex grow', {
                  'max-w-[1028px]': maxWidth === 'lg',
                })}
              >
                <OrderSearch className="grow" />
              </div>
            </div>
          </div>
        </div>

        <ErrorBoundary fallback={<PageLayoutErrorUi />}>
          {children}
        </ErrorBoundary>
      </div>
    </div>
  )
}

const PageLayoutErrorUi = () => {
  return (
    <PageLayoutBody>
      <main className="text-center">
        <h2 className="pb-8 text-3xl sm:text-3xl">Oops, there is an error!</h2>
        <p className="pb-8">
          Please try again, return to home or contact{' '}
          <Anchor
            href={`mailto:${EMAILS.support}`}
            variant="inline-text"
            // oxlint-disable-next-line shadcn/no-restyle -- grandfathered: pre-rollout restyle className on v2 component
            className="text-info-700 underline"
            hideProgressBar
          >
            {EMAILS.support}
          </Anchor>{' '}
          if the issue continues
        </p>
      </main>
    </PageLayoutBody>
  )
}

type AlertBannerProps = ComponentProps<typeof AlertBanner>

type Props = Pick<AlertBannerProps, 'variant' | 'children' | 'onClose'>

const PageLayoutAlertBanner = ({ variant, children, onClose }: Props) => {
  return (
    <AlertBanner
      variant={variant}
      // Modal z-index is 10
      className={clsx('sticky top-18 left-0 z-9', {
        // oxlint-disable-next-line shadcn/no-restyle -- grandfathered: pre-rollout restyle className on v2 component
        'border-b': variant === 'error',
      })}
      onClose={onClose}
    >
      <AlertBanner.Inner className="max-w-[1072px]">
        {children}
      </AlertBanner.Inner>
    </AlertBanner>
  )
}

type PageLayoutBodyProps = {
  children: ReactNode
  /**
   * Default layout has a min-width of 768px (not responsive to smaller screens),
   * because the design always prioritizes the desktop layout.
   *
   * But the consumer can override this by passing in a different min-width.
   *
   * The goal is to eventually all pages be responsive.
   */
  minWidth?: 'md' | 'lg'
  maxWidth?: 'NONE' | 'lg'
  className?: string
  as?: 'div' | 'section'
  containerClassName?: string
}

const PageLayoutBody = ({
  as,
  minWidth,
  maxWidth = 'lg',
  className,
  containerClassName,
  ...props
}: PageLayoutBodyProps) => {
  const Component = as || 'div'

  return (
    <main className={clsx('h-full flex-1 overflow-auto', containerClassName)}>
      <Component
        {...props}
        className={clsx(
          'mx-auto h-full px-4 py-6 lg:px-6',
          {
            'min-w-[768px]': minWidth === 'md',
            'min-w-[1024px]': minWidth === 'lg',
            'max-w-[1072px]': maxWidth === 'lg',
          },
          className,
        )}
      />
    </main>
  )
}

type PageLayoutContentDescriptionProps = {
  children: ReactNode
}
const PageLayoutDescription = ({
  children,
}: PageLayoutContentDescriptionProps) => (
  <p className="text-base font-light">{children}</p>
)

type PageLayoutTitleProps = {
  children: ReactNode
}
const PageLayoutTitle = ({ children }: PageLayoutTitleProps) => (
  <h1 className="font-figtree text-3xl font-semibold">{children}</h1>
)

type PageLayoutHeaderProps = {
  children: ReactNode
}

const PageLayoutHeader = ({ children }: PageLayoutHeaderProps) => {
  return (
    <div className="h-full flex-1">
      <div className="mx-auto mt-px max-w-[1072px]">
        <div className="mx-auto w-full px-4 pt-12 pb-4 md:px-6">{children}</div>
      </div>
    </div>
  )
}

const TransparentPageLayoutHeader = ({ children }: PageLayoutHeaderProps) => {
  return (
    <div className="h-full flex-1">
      <div className="mx-auto mt-px max-w-[1072px]">
        <div className="mx-auto w-full px-4 pt-12 pb-4 md:px-6">{children}</div>
      </div>
    </div>
  )
}

export const PageLayoutRebranded = Object.assign(PageLayoutRootRebranded, {
  Body: PageLayoutBody,
  Header: PageLayoutHeader,
  TransparentHeader: TransparentPageLayoutHeader,
  Title: PageLayoutTitle,
  Description: PageLayoutDescription,
  AlertBanner: PageLayoutAlertBanner,
  ErrorUI: PageLayoutErrorUi,
})
